// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Albert Charlier, Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : pdf viewer
// =============================================================================
// ---Third-party imports
import React, {Component, PureComponent} from 'react'
import {Header, Menu, Dimmer, Loader, Segment} from 'semantic-ui-react'
import {PDFDocument, PDFPage, PDFOutline} from 'uix/chunks'
import {ReflexContainer, ReflexSplitter, ReflexElement} from 'react-reflex'

// ---Framework imports
import {Part} from 'uix/parts'
import log from 'uix/logging'
import connect from 'uix/connect'
import {isNil} from 'ldash'
import {AutoPortal, HeaderSegment} from 'uix/components'
import {prettyBytes} from 'uix/utils'
import {useState, usePersistent} from 'uix/hooks'

// ---Local imports
import styles from './styles.scss'

// ---Globals
class PromiseCancelledException extends Error {
  constructor(message, type) {
    super(message, type)
    this.name = 'PromiseCancelledException'
    this.message = message
    this.type = type
  }
}

const makeCancellable = promise => {
  let isCancelled = false

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (...args) =>
        isCancelled ? reject(new PromiseCancelledException('Promise cancelled')) : resolve(...args),
      error =>
        isCancelled ? reject(new PromiseCancelledException('Promise cancelled')) : reject(error)
    )
  })

  return {
    promise: wrappedPromise,
    cancel() {
      isCancelled = true
    },
  }
}

const cancelRunningTask = runningTask => {
  if (!runningTask || !runningTask.cancel) {
    return
  }
  runningTask.cancel()
}

const isDefined = variable => typeof variable !== 'undefined'

class Ref {
  constructor({num, gen}) {
    this.num = num
    this.gen = gen
  }

  toString() {
    let str = `${this.num}R`
    if (this.gen !== 0) {
      str += this.gen
    }
    return str
  }
}

class OutlineItem extends PureComponent {
  getDestination = async () => {
    const {item, pdf} = this.props

    if (!isDefined(this.destination)) {
      if (typeof item.dest === 'string') {
        this.destination = await pdf.getDestination(item.dest)
      } else {
        this.destination = item.dest
      }
    }

    return this.destination
  }

  getPageIndex = async () => {
    const {pdf} = this.props

    if (!isDefined(this.pageIndex)) {
      const destination = await this.getDestination()
      if (destination) {
        const [ref] = destination
        this.pageIndex = await pdf.getPageIndex(new Ref(ref))
      }
    }

    return this.pageIndex
  }

  getPageNumber = async () => {
    if (!isDefined(this.pageNumber)) {
      this.pageNumber = (await this.getPageIndex()) + 1
    }

    return this.pageNumber
  }

  onClick = async event => {
    const {onClick} = this.props

    event.preventDefault()

    const pageIndex = await this.getPageIndex()
    const pageNumber = await this.getPageNumber()

    if (onClick) {
      onClick({
        pageIndex,
        pageNumber,
      })
    }
  }

  renderSubitems() {
    const {item, ...otherProps} = this.props

    if (!item.items || !item.items.length) {
      return null
    }

    const {items: subitems} = item

    return (
      <ul>
        {subitems.map((subitem, subitemIndex) => (
          <OutlineItem
            key={typeof subitem.destination === 'string' ? subitem.destination : subitemIndex}
            item={subitem}
            {...otherProps}
          />
        ))}
      </ul>
    )
  }

  render() {
    const {item} = this.props

    /* eslint-disable jsx-a11y/anchor-is-valid */
    return (
      <li>
        <a href="#" onClick={this.onClick}>
          {item.title}
        </a>
        {this.renderSubitems()}
      </li>
    )
  }
}
const MIN_OUTLINE = 2

class _PDF extends Component {
  state = {pdf: null, outline: null}
  static storeMap = 'config.apiurl'

  onLoadSuccess = pdf => {
    //~ log('loaded PDF with %o pages %O', pdf.numPages, pdf)
    this.setState({pdf})

    this.loadOutline(pdf)

    let {onLoadSuccess} = this.props
    if (onLoadSuccess) {
      onLoadSuccess(pdf)
    }
  }

  componentWillUnmount() {
    cancelRunningTask(this.runningTask)
  }

  loadOutline = async pdf => {
    const {onOutlineLoaded} = this.props
    this.setState(prevState => {
      if (!prevState.outline) {
        return null
      }
      return {outline: null}
    })

    try {
      const cancellable = makeCancellable(pdf.getOutline())
      this.runningTask = cancellable
      const outline = await cancellable.promise
      this.setState({outline})
      if (onOutlineLoaded) {
        onOutlineLoaded(outline)
      }
    } catch (error) {
      log.error(error)
      this.setState({outline: null})
      if (onOutlineLoaded) {
        onOutlineLoaded(null)
      }
    }
  }

  outline = () => {
    let {onOutlineItemClicked} = this.props
    let {outline, pdf} = this.state

    return (
      <ul className={styles.outline}>
        {outline.map((item, itemIndex) => (
          <OutlineItem
            key={typeof item.destination === 'string' ? item.destination : itemIndex}
            item={item}
            pdf={pdf}
            onClick={onOutlineItemClicked}
          />
        ))}
      </ul>
    )
  }

  render() {
    let {apiurl, otid, pageIndex, showOutline, rotate, onLoadProgress} = this.props
    let {outline} = this.state

    let url = `${apiurl}/download/pdf/${otid}`
    let loader = (
      <Dimmer active inverted>
        <Loader inverted content="Loading..." />
      </Dimmer>
    )
    let outlineActive = showOutline && outline
    return (
      <div style={{display: 'flex', flex: 1}}>
        <ReflexContainer orientation="vertical">
          {outlineActive ? (
            <>
              <ReflexElement key="outline" flex="0.3">
                {this.outline()}
              </ReflexElement>
              <ReflexSplitter className={styles.splitter} />
            </>
          ) : null}

          <ReflexElement key="page" flex={outlineActive ? 0.7 : 1.0}>
            <PDFDocument
              renderMode="canvas"
              file={url}
              onLoadSuccess={this.onLoadSuccess}
              onLoadProgress={onLoadProgress}
              loading={loader}
            >
              <PDFPage pageIndex={pageIndex} loading="" rotate={rotate} />
            </PDFDocument>
          </ReflexElement>
        </ReflexContainer>
      </div>
    )
  }
}

const PDF = connect(
  'PDF',
  _PDF
)

export const PDFViewer = ({request}) => {
  /* render PDF documents
   */
  let [state, _setState] = useState({
    numPages: -1,
    showOutline: true,
    rotate: 0,
    scale: 0.5,
    loadingSize: {loaded: 0, total: 0},
  })
  let [pageIndexes, setPageIndexes] = usePersistent('pageIndexes', {})
  let setState = data => _setState(state => ({...state, ...data}))

  //~ static partStyle = {display: 'flex'}

  let setPageIndex = (otid, idx) => {
    setPageIndexes({...pageIndexes, [otid]: idx})
  }

  let onLoadSuccess = pdf => {
    setState({numPages: pdf.numPages})
  }

  let onOutlineLoaded = outline => {
    setState({outline})
  }

  let onLoadProgress = ({loaded, total}) => {
    //~ log.info(loaded, total)
    setState({loadingSize: {loaded, total}})
  }

  let onOutlineItemClicked = ({pageNumber}) => {
    setPageIndex(request.id, pageNumber - 1)
  }

  let {numPages, showOutline, rotate, outline, loadingSize} = state
  let pageIndex = pageIndexes[request.id] || 0

  let outlineDisabled = !outline || outline.length <= MIN_OUTLINE
  let menu = (
    <Menu>
      <Menu.Item
        title="Show Outline"
        active={showOutline}
        disabled={outlineDisabled}
        icon="content"
        onClick={() => setState({showOutline: !showOutline})}
      />

      <Menu.Item
        title="Rotate Left"
        icon="undo"
        onClick={() => setState({rotate: (rotate + 270) % 360})}
      />
      <Menu.Item
        title="Rotate Right"
        icon="redo"
        onClick={() => setState({rotate: (rotate + 90) % 360})}
      />

      <Menu.Item
        icon="left arrow"
        title="Next Page"
        disabled={pageIndex == 0}
        onClick={() => setPageIndex(request.id, pageIndex - 1)}
      />
      <Menu.Item content={`${pageIndex + 1}/${numPages}`} />
      <Menu.Item
        icon="right arrow"
        title="Next Page"
        disabled={pageIndex >= numPages - 1}
        onClick={() => setPageIndex(request.id, pageIndex + 1)}
      />
    </Menu>
  )

  showOutline = showOutline && !outlineDisabled
  let loading = loadingSize.total > loadingSize.loaded
  let subheader = `${prettyBytes(loadingSize.total)}`
  if (loading) {
    subheader = `${subheader} (${prettyBytes(loadingSize.loaded)})`
  }
  return (
    <HeaderSegment
      header="PDF"
      flex
      overflowInner
      subheader={subheader}
      loading={loading}
      icon="file pdf outline"
      menu={menu}
    >
      <PDF
        otid={request.id}
        showOutline={showOutline}
        pageIndex={pageIndexes[request.id] || 0}
        onOutlineItemClicked={onOutlineItemClicked}
        onOutlineLoaded={onOutlineLoaded}
        onLoadSuccess={onLoadSuccess}
        onLoadProgress={onLoadProgress}
        rotate={rotate}
      />
    </HeaderSegment>
  )
}
